import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, query, collection, getDocs, where, limit, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import LoadingSpinner from '../../components/LoadingSpinner';

const OnboardingStep2 = ({ onComplete, onSkip }) => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joinCode, setJoinCode] = useState('');

  useEffect(() => {
    if (currentUser) {
      loadOrganizations();
    }
  }, [currentUser]);

  const loadOrganizations = async () => {
    if (!currentUser?.uid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Load public organizations (not global, not private)
      const orgsQuery = query(
        collection(db, 'organizations'),
        where('status', '==', 'active'),
        where('type', '!=', 'global'),
        limit(20)
      );

      const snapshot = await getDocs(orgsQuery);
      const orgs = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        // Skip if user is already a member
        if (currentUser.activeOrgs?.includes(docSnap.id) || 
            currentUser.organizations?.some((o) => o.id === docSnap.id)) {
          return;
        }
        orgs.push({
          id: docSnap.id,
          name: data.name || 'Organization',
          status: data.status || 'active',
          tagline: data.tagline || data.description || '',
          logoURL: data.logoUrl || data.logoURL || '',
        });
      });

      setOrganizations(orgs);
    } catch (error) {
      console.error('Error loading organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinByCode = async () => {
    if (!joinCode.trim()) {
      alert('Please enter an organization code');
      return;
    }

    setJoining(true);
    try {
      const orgsQuery = query(
        collection(db, 'organizations'),
        where('orgCode', '==', joinCode.trim().toUpperCase()),
        where('orgCodeEnabled', '==', true),
        limit(1)
      );

      const snapshot = await getDocs(orgsQuery);

      if (snapshot.empty) {
        alert('Organization code not found');
        setJoining(false);
        return;
      }

      const orgDoc = snapshot.docs[0];
      await handleJoinOrganization(orgDoc.id, orgDoc.data());
    } catch (error) {
      console.error('Error joining by code:', error);
      alert('Failed to join organization');
      setJoining(false);
    }
  };

  const handleJoinOrganization = async (orgId, orgData) => {
    if (!currentUser?.uid) return;

    setJoining(true);
    try {
      // Check if already a member
      const memberRef = doc(db, 'organizations', orgId, 'members', currentUser.uid);
      const memberSnap = await getDoc(memberRef);

      if (memberSnap.exists()) {
        alert('You are already a member of this organization');
        setJoining(false);
        return;
      }

      // Add user to organization members
      await setDoc(memberRef, {
        role: 'member',
        joinedAt: serverTimestamp(),
        joinedVia: 'onboarding',
      });

      // Add organization to user's organizations subcollection
      await setDoc(
        doc(db, 'users', currentUser.uid, 'organizations', orgId),
        {
          role: 'member',
          joinedAt: serverTimestamp(),
        }
      );

      // Update user document with organization reference
      await updateDoc(doc(db, 'users', currentUser.uid), {
        activeOrgs: arrayUnion(orgId),
        organizations: arrayUnion({
          id: orgId,
          name: orgData?.name || 'Organization',
          role: 'member',
        }),
        onboardingStep: 3,
      });

      // Continue to next step
      onComplete();
    } catch (error) {
      console.error('Error joining organization:', error);
      alert('Failed to join organization. Please try again.');
      setJoining(false);
    }
  };

  const handleSkip = async () => {
    if (currentUser?.uid) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          onboardingStep: 3,
        });
      } catch (error) {
        console.error('Error updating onboarding step:', error);
      }
    }
    onSkip();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0076BE] to-[#5ABA47]">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-xl shadow-2xl p-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-[#0076BE] mb-2">
              Join an Organization
            </h2>
            <p className="text-gray-600">Step 2 of 4</p>
          </div>

          <p className="text-gray-700 mb-6 text-center">
            Connect with a church or organization to collaborate with others in your mission journey.
          </p>

          {/* Join by Code */}
          <div className="mb-8">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Have an organization code?
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0076BE]"
                maxLength={10}
              />
              <button
                onClick={handleJoinByCode}
                disabled={joining || !joinCode.trim()}
                className="px-6 py-2 bg-[#5ABA47] text-white font-semibold rounded-lg hover:bg-[#4a9d3a] disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {joining ? 'Joining...' : 'Join'}
              </button>
            </div>
          </div>

          {/* Organizations List */}
          {organizations.length > 0 ? (
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Or browse organizations:
              </h3>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {organizations.map((org) => (
                  <button
                    key={org.id}
                    onClick={() => handleJoinOrganization(org.id, org)}
                    disabled={joining}
                    className="w-full flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-[#0076BE] transition-colors disabled:opacity-50"
                  >
                    {org.logoURL && (
                      <img
                        src={org.logoURL}
                        alt={org.name}
                        className="w-12 h-12 rounded-lg object-cover mr-4"
                      />
                    )}
                    <div className="flex-1 text-left">
                      <h4 className="font-semibold text-gray-900">{org.name}</h4>
                      {org.tagline && (
                        <p className="text-sm text-gray-600">{org.tagline}</p>
                      )}
                    </div>
                    <svg
                      className="w-5 h-5 text-[#0076BE]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mb-6 text-center text-gray-600">
              <p>No public organizations available at this time.</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              onClick={handleSkip}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
            >
              Skip for Now
            </button>
            <button
              onClick={() => navigate('/discover-communities')}
              className="flex-1 px-6 py-3 bg-[#0076BE] text-white font-semibold rounded-lg hover:bg-[#005f98] transition-colors"
            >
              Browse All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingStep2;
